import React from "react";
import cx from "classnames";
import { Meta, Text } from "../../Type";
import { useEventDetailContext } from "../../../contexts/eventDetailProvider";
import { useAuth } from "../../../contexts/AuthProvider";
import { coordWeldMap } from "../../../helpers/coordWeldMap";
import {
  MILLISECONDS_IN_SECONDS,
  METERS_IN_KM
} from "../../../helpers/constants";
import styles from "./index.module.scss";
import DateTime from "../../DateTime";
import { kpToSp, kpToMp, kpToFt } from "../../AnalysisCharts/BaseChart";
import { PigUpdateMetadata } from "@hifieng/common/dist/types/graphql";

export const detectIOS = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (
    userAgent.search("iphone") > -1 ||
    userAgent.search("ipad") > -1 ||
    userAgent.search("ipod") > -1
  ) {
    return true;
  } else {
    return false;
  }
};
const isIOS = detectIOS();

export const copyToClipboard = () => {
  // Get the text field
  const copyText = document.getElementById("copyCoordinates");

  // Select the text field
  if (copyText !== null) {
    const inner = copyText.innerText;
    if (inner !== undefined) {
      // Copy the text inside the text field
      navigator.clipboard.writeText(inner);
      // Alert the copied text
      /* eslint-disable no-alert */
      alert(`Copied the coordinates ${inner} to clipboard.`);
    }
  }
};

/**
 * Validates if a string is a valid Unix timestamp in seconds (10 digits).
 * Unix timestamps should be in seconds, not milliseconds.
 * @param {string | null | undefined} timestampStr - The timestamp string to validate
 * @returns {boolean} True if valid 10-digit Unix timestamp, false otherwise
 */
export function isUnixTimestampString(
  timestampStr: string | null | undefined
): boolean {
  // console.log("isUnixTimestampString", timestampStr);
  if (!timestampStr) {
    return false;
  }
  const trimmed = timestampStr.trim();
  // console.log("trimmed timestamp", trimmed);

  // This component converts the value from seconds to milliseconds,
  // so only accept second-based Unix timestamps.
  // checks for 10 digit UNIX timestamps, which is seconds.
  if (!/^\d{10}$/.test(trimmed)) {
    return false;
  }
  const timestamp = Number(trimmed);

  return Number.isInteger(timestamp) && timestamp > 0;
}

export type PigMetadataRow = {
  name: string;
  value: string;
  isTimestamp: boolean;
};

export type ParsedPigUpdateMetadata = {
  rows: Record<string, PigMetadataRow>;
  hasNextPoiEta: boolean;
  hasNextPoi: boolean;
};

/**
 * Parses pig update metadata array into organized rows with timestamp detection.
 * Identifies special fields like "Next POI ETA" and "Next POI" for conditional rendering.
 * @param {Array<PigUpdateMetadata>} pigUpdateMetadata - Array of pig update metadata entries
 * @returns {ParsedPigUpdateMetadata} Parsed metadata with rows and special field flags
 */
export function parsePigUpdateMetadata(
  pigUpdateMetadata: Array<PigUpdateMetadata>
): ParsedPigUpdateMetadata {
  console.log("parsePigUpdateMetadata calc");
  const result: ParsedPigUpdateMetadata = {
    rows: {},
    hasNextPoiEta: false,
    hasNextPoi: false
  };

  Object.entries(pigUpdateMetadata).forEach(([key, pigUpdateMetadata]) => {
    // Guard clause - skip entries without valid name or value
    if (!pigUpdateMetadata.name || !pigUpdateMetadata.value) {
      return;
    }
    const nameLower = pigUpdateMetadata.name.toLowerCase();
    const isTimestamp =
      (nameLower.includes("eta") || nameLower.includes("time")) &&
      isUnixTimestampString(pigUpdateMetadata.value);

    // Detect if the metadata entry is related to "Next POI ETA" or "Next POI" based on its name
    // This is only reached if the entry has a valid name and value. If the value is null then guard clause skips
    if (nameLower.includes("point of interest") || nameLower.includes("poi")) {
      if (nameLower.includes("eta")) {
        result.hasNextPoiEta = true;
      } else if (nameLower.includes("next")) {
        result.hasNextPoi = true;
      }
    }

    result.rows[key] = {
      name: pigUpdateMetadata.name,
      value: pigUpdateMetadata.value,
      isTimestamp
    };
  });

  return result;
}

const EventInfo = () => {
  const { event } = useEventDetailContext();
  const { permissions } = useAuth();

  // Guard clause - exit early if no event
  if (!event) {
    return null;
  }

  let estimatedArrival: number | undefined;
  const nextPoi = event.eventMeta && event.eventMeta.nextPoi;
  const speed = event.eventMeta && event.eventMeta.speed;
  const timestamp: number | undefined = event.eventMeta
    ? event.eventMeta.timestamp
    : undefined;

  if (nextPoi && speed && timestamp) {
    const distanceInKm = nextPoi.post - event.post;
    const secondsToArrival = Math.floor((distanceInKm * METERS_IN_KM) / speed);
    estimatedArrival = timestamp + secondsToArrival;
  }

  const COORDINATE_DIGITS = 6;
  const coordinatesString = `${event.coordinates.latitude.toFixed(
    COORDINATE_DIGITS
  )},${event.coordinates.longitude.toFixed(COORDINATE_DIGITS)}`;
  console.log("EventInfo Render", event.eventMeta);

  const parsedMeta = event.eventMeta
    ? event.eventMeta.pigUpdateMetadata
      ? parsePigUpdateMetadata(event.eventMeta.pigUpdateMetadata)
      : null
    : null;

  const shouldShowMilePost =
    permissions.user.imperial ||
    ["enbridge_line_3", "enbridge_line_4"].includes(event.pipelineId);

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          {event.type === "pig" || event.type === "train"
            ? "RUN ID"
            : "pipeline"}
        </Meta>
        <Text className={styles.Text} component="span" size="small">
          {event.type === "train" && event.eventMeta
            ? event.eventMeta.railId
            : event.pipeline.name}
          {(event.type === "pig" || event.type === "train") && event.eventMeta
            ? ` - Run ID ${event.eventMeta.runId}`
            : ""}
        </Text>
      </div>
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          {shouldShowMilePost
            ? "Mile Post"
            : permissions.user.station
            ? "Station Point"
            : permissions.user.ft
            ? "Feet"
            : "KM Post"}
        </Meta>
        <Text className={styles.Text} component="span" size="small">
          {shouldShowMilePost
            ? `MP ${kpToMp(event.post)}`
            : permissions.user.station
            ? `SP ${kpToSp(event.post)}`
            : permissions.user.ft
            ? `${kpToFt(event.post)} ft`
            : `KP ${event.post}`}
        </Text>
      </div>
      {event.pipeline.id === "suncor_icp" ? (
        <div className={styles.Row}>
          <Meta component="h5" size="small" dark>
            Weld ID
          </Meta>
          <Text className={styles.Text} component="span" size="small">
            {coordWeldMap(coordinatesString)}
          </Text>
        </div>
      ) : (
        undefined
      )}
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          Start Time
        </Meta>
        <Text className={styles.Text} component="span" size="small">
          {event.startTime ? (
            <DateTime
              timestamp={event.startTime * MILLISECONDS_IN_SECONDS}
              length="long"
            />
          ) : (
            "-"
          )}
        </Text>
      </div>
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          End Time
        </Meta>
        <Text className={styles.Text} component="span" size="small">
          {event.endTime ? (
            <DateTime
              timestamp={event.endTime * MILLISECONDS_IN_SECONDS}
              length="long"
            />
          ) : (
            "-"
          )}
        </Text>
      </div>
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          Archived
        </Meta>
        <Text className={styles.Text} component="span" size="small">
          {event.archived && event.archivedDate ? (
            <DateTime
              timestamp={event.archivedDate * MILLISECONDS_IN_SECONDS}
              length="long"
            />
          ) : (
            "-"
          )}
        </Text>
      </div>
      <div className={styles.Row}>
        <Meta component="h5" size="small" dark>
          Lat/Lng
        </Meta>
        {isIOS ? (
          <button type="button" onClick={copyToClipboard}>
            <Text
              className={styles.Text}
              component="span"
              size="small"
              id="copyCoordinates"
            >
              {`${event.coordinates.latitude}, ${event.coordinates.longitude}`}
            </Text>
          </button>
        ) : (
          <a
            href={`https://maps.google.com/maps?q=loc:${event.coordinates.latitude},${event.coordinates.longitude}`}
            className={styles.Link}
            target="__blank"
          >
            <Text className={styles.Text} component="span" size="small">
              {`${event.coordinates.latitude}, ${event.coordinates.longitude}`}
            </Text>
          </a>
        )}
      </div>
      {event.type === "pig" && event.eventMeta ? (
        <>
          <hr className={styles.Divider} />
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Speed
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.eventMeta.speed}m/second
            </Text>
          </div>
          {(!parsedMeta || !parsedMeta.hasNextPoiEta) && (
            // skips rendering Next POI and ETA if that information is already included in pig update metadata rows to avoid duplication
            <div className={styles.Row}>
              <Meta component="h5" size="small" dark>
                Next POI
              </Meta>
              <Text className={styles.TextPoi} component="span" size="small">
                {event.eventMeta.nextPoi
                  ? `${event.eventMeta.nextPoi.type} ${event.eventMeta.nextPoi.post}`
                  : "N/A"}
              </Text>
            </div>
          )}
          {estimatedArrival && (!parsedMeta || !parsedMeta.hasNextPoiEta) && (
            // skips rendering Next POI and ETA if that information is already included in pig update metadata rows to avoid duplication
            <div className={styles.Row}>
              <Meta component="h5" size="small" dark>
                Arrival (EST.)
              </Meta>
              <Text className={styles.Text} component="span" size="small">
                <DateTime
                  timestamp={estimatedArrival * MILLISECONDS_IN_SECONDS}
                  length="long"
                />
              </Text>
            </div>
          )}
          {parsedMeta &&
            Object.values(parsedMeta.rows).map(row => (
              <div className={styles.Row} key={row.name}>
                <Meta component="h5" size="small" dark>
                  {row.name || "-"}
                </Meta>
                <Text
                  className={styles.Text}
                  title={row.value}
                  component="span"
                  size="small"
                >
                  {row.isTimestamp ? (
                    <DateTime
                      timestamp={Number(row.value) * MILLISECONDS_IN_SECONDS}
                      length="long"
                    />
                  ) : (
                    row.value || "-"
                  )}
                </Text>
              </div>
            ))}
        </>
      ) : null}
      {permissions.user["view-admin-event"] ? (
        <>
          <hr className={styles.Divider} />
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Channel
            </Meta>
            <Text className={styles.Text} component="span" size="small">
              {event.channel ? event.channel : "-"}
            </Text>
          </div>
          <div className={styles.Row}>
            <Meta component="h5" size="small" dark>
              Guid
            </Meta>
            <Text
              className={cx(styles.Text, styles.Wrap)}
              component="span"
              size="small"
              disambiguation
            >
              {event ? event.id.toLowerCase() : "-"}
            </Text>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default EventInfo;
